import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { API_PREFIX } from "../config";
import useDebouncedValue from "../hooks/useDebouncedValue";
import usePagedList from "../hooks/usePagedList";
import { groupLabel } from "../utils/identity";
import "./GroupPicker.css";

const GROUPS_PATH = `${API_PREFIX}/auth/groups`;

/** First-page URL of the group search; the page size is the backend's default
 * (GROUPS_PAGE_DEFAULT) so it stays env-configurable in one place. */
export const groupSearchPath = (query) =>
  (query ? `${GROUPS_PATH}?q=${encodeURIComponent(query)}` : GROUPS_PATH);

/** Pick one identity-provider group: debounced server-side search over name /
 * external id, cursor-paginated ("load more"). `value` is the selected group's
 * id; `onSelect` receives the whole group so the caller can show its name. */
export default function GroupPicker({ value, onSelect, disabled = false }) {
  const { t } = useTranslation();
  const searchId = useId();
  const radioName = useId();
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query.trim());
  const { items, loading, error, hasMore, loadMore } = usePagedList(groupSearchPath(debounced));

  return (
    <fieldset className="ds-field group-picker">
      <legend className="ds-visually-hidden">{t("adminCollections.selectGroup")}</legend>
      <label className="ds-label" htmlFor={searchId}>{t("adminCollections.groupSearch")}</label>
      <input
        id={searchId}
        type="search"
        className="ds-input ds-input--sm"
        value={query}
        placeholder={t("adminCollections.groupSearchPlaceholder")}
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
      />
      {error && <div className="ds-alert ds-alert--danger" role="alert">{error}</div>}
      <div className="group-picker-list">
        {items.map((g) => (
          <label key={g.id} className="group-picker-option">
            <input
              type="radio"
              name={radioName}
              checked={value === g.id}
              disabled={disabled}
              onChange={() => onSelect(g)}
            />
            <span>{groupLabel(g)}</span>
            {g.display_name && g.external_id && <span className="ds-meta ds-mono">{g.external_id}</span>}
          </label>
        ))}
      </div>
      {loading && <span className="ds-help">{t("adminCollections.groupsLoading")}</span>}
      {!loading && !error && items.length === 0 && (
        <span className="ds-help">{t("adminCollections.groupsEmpty")}</span>
      )}
      {hasMore && !loading && (
        <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={disabled} onClick={loadMore}>
          {t("adminCollections.loadMoreGroups")}
        </button>
      )}
    </fieldset>
  );
}
